/**
 * The number a question carries on the paper it was read from.
 *
 * Extraction stores the label as printed (`Question.sourceQuestionNumber`):
 * "01.", "Q7", "2(a)", "12)". A reader wants "1", "7", "2(a)", "12". Positions
 * are not a substitute once a question is removed: printed question 8 sits at
 * position 7 of the test, while the student's sheet still says 8.
 */

/** "01." → "1", "Q7" → "7", "2(a)" → "2(a)"; undefined when there is nothing. */
export function paperNumberLabel(raw: string | number | null | undefined): string | undefined {
    if (raw === null || raw === undefined) return undefined;
    let s = String(raw).trim();
    s = s.replace(/^(?:q(?:uestion)?\.?\s*|no\.?\s*)/i, '');   // "Q7", "Question 7", "No. 7"
    s = s.replace(/[.:\]]+$/, '');                             // "7.", "7:"
    // "7)" is a label's punctuation, but the ")" of "2(a)" closes a bracket.
    while (s.endsWith(')') && (s.match(/\(/g)?.length ?? 0) < (s.match(/\)/g)?.length ?? 0)) {
        s = s.slice(0, -1).replace(/[.:]+$/, '');
    }
    s = s.replace(/^0+(?=\d)/, '');                            // "01" → "1"
    return s ? s : undefined;
}

/**
 * The label for each question, in order: its printed number when EVERY
 * question has one and no two are the same, else its 1-based position for all.
 * Mixing the two, or two questions both labelled "1" (a paper whose sections
 * each start at 1), would make a number the student reads mean two things.
 */
export function questionLabels(printed: ReadonlyArray<string | number | null | undefined>): string[] {
    const labels = printed.map(paperNumberLabel);
    const usable = labels.every((l): l is string => !!l) && new Set(labels).size === labels.length;
    return usable ? (labels as string[]) : printed.map((_, i) => String(i + 1));
}

/**
 * Numbering by section, for a paper that restarts its numbers in each one:
 * "A:6, B:6, C:4" → A1…A6, B1…B6, C1…C4. Each section is a label (letters or
 * digits) and its question count, in paper order. The numbers it gives are
 * stored as each question's printed number, so a student who writes "B2" is
 * joined to the right question. See
 * docs/FeatureDesigns/solution-analysis/solution-page-reader-target.md, "Labels".
 */
export function parseSectionNumbering(spec: string): { numbers: string[] } | { error: string } {
    const parts = spec.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
    if (!parts.length) return { error: 'Give each section as LABEL:COUNT, e.g. A:6, B:6' };
    const numbers: string[] = [];
    const labels = new Set<string>();
    for (const part of parts) {
        const m = /^([A-Za-z0-9]{1,4})\s*[:=x×]\s*(\d{1,3})$/.exec(part);
        if (!m) return { error: `"${part}" is not LABEL:COUNT` };
        const label = m[1].toUpperCase();
        const count = Number(m[2]);
        if (count < 1) return { error: `Section ${label} has no questions` };
        if (labels.has(label)) return { error: `Section ${label} appears twice` };
        labels.add(label);
        for (let i = 1; i <= count; i++) numbers.push(`${label}${i}`);
    }
    return { numbers };
}

/**
 * A section numbering to suggest for a paper whose printed numbers restart —
 * "1…6, 1…6, 1…4" → "A:6, B:6, C:4" — or null when the numbers do not repeat,
 * or do not run 1, 2, 3… within each run. Letters A, B, C… in paper order.
 */
export function suggestSectionNumbering(printed: ReadonlyArray<string | number | null | undefined>): string | null {
    const labels = printed.map(paperNumberLabel);
    if (labels.length < 2 || labels.some(l => !l) || new Set(labels).size === labels.length) return null;
    const runs: number[] = [];
    for (const l of labels) {
        const n = Number(l);
        if (!Number.isInteger(n)) return null;
        if (n === 1) runs.push(1);
        else if (runs.length && n === runs[runs.length - 1] + 1) runs[runs.length - 1] = n;
        else return null;
    }
    if (runs.length < 2 || runs.length > 26) return null;
    return runs.map((count, i) => `${String.fromCharCode(65 + i)}:${count}`).join(', ');
}
